// The ways to make something in Simple, each the Created AI Simple prototype's tile in its words,
// and where it goes (spec ui-perf/simple-create-picker): the sections (Ads, Videos, Images) and
// Create's picker draw them from here, so a tile reads the same everywhere.
import { Link } from "@tanstack/react-router";
import {
  AudioWaveform,
  Camera,
  CircleUserRound,
  Clapperboard,
  Copy,
  Film,
  Image as ImageIcon,
  Layers,
  LayoutGrid,
  LayoutTemplate,
  type LucideIcon,
  Mic,
  Music,
  RotateCw,
  Sparkles,
  UserRoundPlus,
} from "lucide-react";

import { SoonControl } from "../Soon";
import type { GuidedKind } from "./guided";
import type { useOwnPictures } from "./home-pictures";
import classes from "./simple-sections.module.css";
import { SOUND_PAGES, type SoundSlug } from "./sounds";
import { freshStart } from "./start";
import { TileBody } from "./Tile";
import type { VideoTool } from "./video-tools";

/** Where a tile goes: a flow page of Simple's, the guided page on a kind, a sound's page, or none yet. */
type Way =
  | { page: "ugc-video-ad" | "image-ad" | "actor" | "fix-image" }
  | { make: GuidedKind }
  | { sound: SoundSlug }
  | { soon: true };

interface MakeTileDef {
  name: string;
  line: string;
  /** The tag over its picture: what it makes. */
  kind: string;
  icon: LucideIcon;
  way: Way;
}

const SOON = { soon: true } as const;

/** A sound page's name and line, as its page says them. */
const words = (sound: SoundSlug) => ({ name: SOUND_PAGES[sound].name, line: SOUND_PAGES[sound].line });

export const MAKE_TILES = {
  // "Small new" wizards in the prototype, each its own PR.
  set: {
    name: "A set of different ads",
    line: "Different ideas, people and formats in one go, so Meta treats each one as its own ad.",
    kind: "Video",
    icon: LayoutGrid,
    way: SOON,
  },
  ugc: {
    name: "UGC video ad",
    line: "An AI actor talks about your product, to the camera or in a real scene.",
    kind: "Video",
    icon: CircleUserRound,
    way: { page: "ugc-video-ad" },
  },
  static: {
    name: "Ad static",
    line: "A static ad with your headline and button.",
    kind: "Image",
    icon: ImageIcon,
    way: { page: "image-ad" },
  },
  animate: {
    name: "Animate a product or image",
    line: "A short video from a photo.",
    kind: "Video",
    icon: Film,
    way: { make: "video" },
  },
  winner: {
    name: "More like an ad that works",
    line: "Keep what works and change the parts Meta looks at: the person, the opening line, the setting or the format.",
    kind: "Video",
    icon: RotateCw,
    way: SOON,
  },
  // The multi-shot storyboard is not built (G16, issue 981).
  storyboard: {
    name: "Storyboard ad",
    line: "A multi-shot ad: script, actor and shots, built for you.",
    kind: "Video",
    icon: Clapperboard,
    way: SOON,
  },
  // Other brands' ads are the ads area's Inspiration, not built.
  copy: {
    name: "Copy a winning ad",
    line: "Pick a live ad from another brand and make it yours.",
    kind: "Ads",
    icon: Copy,
    way: SOON,
  },
  templates: {
    name: "Start from a template",
    line: "Podcast ad, actor swap, hooks and more. Proven formats, your product.",
    kind: "Templates",
    icon: LayoutTemplate,
    way: { make: "ad" },
  },
  photos: {
    name: "Product photos",
    line: "Your product in a new scene.",
    kind: "Image",
    icon: Camera,
    way: { make: "image" },
  },
  actor: {
    name: "Make an actor",
    line: "A new person for your ads. You get 4 to choose from.",
    kind: "Image",
    icon: UserRoundPlus,
    way: { page: "actor" },
  },
  // A "Small new" wizard in the prototype: its own page (spec ui-perf/simple-fix-image).
  tweak: {
    name: "Fix this image",
    line: "Small changes to an image you made.",
    kind: "Image",
    icon: Sparkles,
    way: { page: "fix-image" },
  },
  // Layerize starts in Pro's image form, but only Classic's Studio opens the layer editor after
  // it: a job's detail says Layers is Coming soon (spec ui-perf/simple-images).
  layers: {
    name: "Edit text and layout",
    line: "Split an image into layers you can move and change.",
    kind: "Image",
    icon: Layers,
    way: SOON,
  },
  // Simple's sound pages (spec ui-perf/simple-sounds), their words theirs.
  voiceover: { ...words("voiceover"), kind: "Audio", icon: Mic, way: { sound: "voiceover" } },
  music: { ...words("music"), kind: "Audio", icon: Music, way: { sound: "music" } },
  sfx: { ...words("sound-effect"), kind: "Audio", icon: AudioWaveform, way: { sound: "sound-effect" } },
} as const satisfies Record<string, MakeTileDef>;

export type MakeTileId = keyof typeof MAKE_TILES;

const PAGES = {
  "ugc-video-ad": "/teams/$teamId/brands/$brandId/simple/ugc-video-ad",
  "image-ad": "/teams/$teamId/brands/$brandId/simple/image-ad",
  actor: "/teams/$teamId/brands/$brandId/simple/actor",
  "fix-image": "/teams/$teamId/brands/$brandId/simple/fix-image",
} as const;

interface MakeTileProps {
  id: MakeTileId;
  teamId: string;
  brandId: string;
  /** The brand's own pictures of its kind, where the section reads them; else its icon. */
  own?: ReturnType<typeof useOwnPictures> | undefined;
  /** Picked: Create's picker closes. */
  onPick?: (() => void) | undefined;
}

/**
 * A way to make something, as the prototype's tile: its link, or Coming soon. A link to the
 * guided page starts it anew, as Create did (spec ui-perf/simple-shell, decision 4).
 */
export function MakeTile({ id, teamId, brandId, own, onPick }: MakeTileProps) {
  const tile: MakeTileDef = MAKE_TILES[id];
  const body = <TileBody name={tile.name} line={tile.line} kind={tile.kind} icon={tile.icon} own={own} />;
  const params = { teamId, brandId };
  if ("soon" in tile.way) {
    return (
      <SoonControl block className={classes.tile} label={tile.name}>
        {body}
      </SoonControl>
    );
  }
  if ("sound" in tile.way) {
    return (
      <Link
        to="/teams/$teamId/brands/$brandId/simple/audio/$sound"
        params={{ teamId, brandId, sound: tile.way.sound }}
        className={classes.tile}
        onClick={onPick}
      >
        {body}
      </Link>
    );
  }
  if ("make" in tile.way) {
    return (
      <Link
        to="/teams/$teamId/brands/$brandId/simple/make"
        params={params}
        search={{ kind: tile.way.make }}
        state={freshStart}
        className={classes.tile}
        onClick={onPick}
      >
        {body}
      </Link>
    );
  }
  return (
    <Link to={PAGES[tile.way.page]} params={params} className={classes.tile} onClick={onPick}>
      {body}
    </Link>
  );
}

/** A video tool's tile (`video-tools.ts`): its page in Simple, or Coming soon. */
export function ToolTile({ tool, teamId, brandId, onPick }: { tool: VideoTool; teamId: string; brandId: string; onPick?: (() => void) | undefined }) {
  const body = <TileBody name={tool.name} line={tool.line} kind="Video" icon={tool.icon} />;
  if (tool.page === undefined) {
    return (
      <SoonControl block className={classes.tile} label={tool.name}>
        {body}
      </SoonControl>
    );
  }
  return (
    <Link
      to="/teams/$teamId/brands/$brandId/simple/videos/$tool"
      params={{ teamId, brandId, tool: tool.page.slug }}
      className={classes.tile}
      onClick={onPick}
    >
      {body}
    </Link>
  );
}
